import { PILLARS } from './pillars';

/**
 * Calculate scores for a profile in the new intent-first model.
 * drivers = { connection: { importance: N }, ... }
 * availability = single 1-10 score
 */
export const calculateProfileScores = (profile) => {
  const { drivers, availability, intent } = profile;
  if (!drivers) return null;

  const pillarScores = PILLARS.map(pillar => {
    const importance = drivers[pillar.id]?.importance || 0;
    // Effective gap = importance vs overall availability (scaled)
    const effectiveAccess = Math.round(availability * (importance / 10));
    const gap = Math.max(0, importance - effectiveAccess);

    return {
      pillarId: pillar.id,
      importance,
      effectiveAccess,
      gap,
      isDriver: importance >= 7,
      isAligned: gap <= 2 && importance >= 5,
      isMisaligned: gap > 2 && importance >= 5
    };
  });

  // Sort by importance (descending)
  const sortedByImportance = [...pillarScores].sort((a, b) => b.importance - a.importance);
  const topDrivers = sortedByImportance.filter(s => s.importance >= 6).slice(0, 3);
  const topGaps = sortedByImportance.filter(s => s.isMisaligned).sort((a, b) => b.gap - a.gap);
  const aligned = pillarScores.filter(s => s.isAligned);

  // Overall alignment level
  const avgImportance = pillarScores.reduce((s, p) => s + p.importance, 0) / pillarScores.length;
  let alignmentLevel;
  if (availability >= 7 && topGaps.length === 0) {
    alignmentLevel = 'high';
  } else if (availability >= 5 && topGaps.length <= 1) {
    alignmentLevel = 'moderate';
  } else {
    alignmentLevel = 'low';
  }

  return {
    pillarScores,
    topDrivers,
    topGaps,
    aligned,
    availability,
    avgImportance,
    alignmentLevel,
    intentLabels: intent || []
  };
};

/**
 * Cross-profile comparison (simplified)
 */
export const compareProfiles = (profiles) => {
  if (!profiles || profiles.length < 2) return null;

  const analyses = profiles.map(p => ({
    label: p.label,
    scores: calculateProfileScores(p)
  }));

  const insights = [];

  // Different drivers across contexts
  const driverSets = analyses.map(a => a.scores.topDrivers.map(d => d.pillarId));
  const allDrivers = [...new Set(driverSets.flat())];
  const sharedDrivers = allDrivers.filter(d => driverSets.every(set => set.includes(d)));
  const uniqueDrivers = allDrivers.filter(d => !sharedDrivers.includes(d));

  if (sharedDrivers.length > 0) {
    const names = sharedDrivers.map(id => PILLARS.find(p => p.id === id)?.shortName).filter(Boolean);
    insights.push({
      type: 'shared',
      text: `${names.join(' and ')} ${names.length === 1 ? 'matters' : 'matter'} across contexts. This appears to be a core part of what works for you.`
    });
  }

  if (uniqueDrivers.length > 0) {
    insights.push({
      type: 'context-specific',
      text: 'Your needs shift depending on context. What drives one experience is different from what drives another.'
    });
  }

  // Alignment differences
  const alignments = analyses.map(a => a.scores.alignmentLevel);
  if (alignments.includes('high') && alignments.includes('low')) {
    const highLabel = analyses.find(a => a.scores.alignmentLevel === 'high')?.label;
    const lowLabel = analyses.find(a => a.scores.alignmentLevel === 'low')?.label;
    insights.push({
      type: 'fulfillment',
      text: `You appear more aligned in "${highLabel}" than in "${lowLabel}". This suggests the conditions differ, not your capacity.`
    });
  }

  return { insights };
};
